import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import {
  FACILITY_TYPES,
  SPORT_OPTIONS,
  PRODUCT_OPTIONS,
  TIMELINES,
} from "../lib/site";

export const Field = ({ label, children, required, hint }) => (
  <label className="block">
    <span className="mako-label text-[#7A7F85]">
      {label}
      {required && <span className="text-[#0F4C81]"> *</span>}
    </span>
    <div className="mt-3">{children}</div>
    {hint && <span className="mt-2 block text-xs text-[#7A7F85]">{hint}</span>}
  </label>
);

export const inputCls =
  "w-full border border-[#111111]/15 bg-white px-4 py-3.5 text-sm text-[#111111] placeholder:text-[#7A7F85]/70 transition-[border-color,box-shadow] duration-300 focus:border-[#0F4C81] focus:outline-none focus:ring-1 focus:ring-[#0F4C81]";

export const Chip = ({ active, children, ...rest }) => (
  <button
    type="button"
    {...rest}
    className={`border px-4 py-2.5 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
      active
        ? "border-[#0F4C81] bg-[#0F4C81] text-white"
        : "border-[#111111]/15 bg-white text-[#3B3B3B] hover:border-[#0F4C81] hover:text-[#0F4C81]"
    }`}
  >
    {children}
  </button>
);

const EMPTY = {
  full_name: "",
  organisation: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  facility_type: "",
  sports: [],
  products: [],
  area: "",
  timeline: "",
  message: "",
  honeypot: "",
};

export const readFiles = async (files) => {
  const picked = Array.from(files).slice(0, 3);
  const out = [];
  for (const file of picked) {
    if (file.size > 2 * 1024 * 1024) {
      toast.error(`${file.name} is larger than 2 MB and was skipped.`);
      continue;
    }
    const data = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
    out.push(`${file.name}::${data}`);
  }
  return out;
};

export const QuoteForm = ({ source = "quote-form", onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
    if (!/^[\d+\-\s()]{6,}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.city.trim()) e.city = "Please tell us your city.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setBusy(true);
    try {
      const attachments = await readFiles(files);
      await http.post("/enquiries", { ...form, attachments, source });
      setDone(true);
      setForm(EMPTY);
      setFiles([]);
      toast.success("Enquiry received. MAKO will be in touch.");
      if (onDone) onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div
        data-testid="quote-form-success"
        className="border border-[#0F4C81]/25 bg-[#F5F5F2] p-10 sm:p-14"
      >
        <p className="mako-label text-[#0F4C81]">Enquiry received</p>
        <h3 className="mt-6 font-display text-2xl font-bold leading-tight text-[#111111] sm:text-3xl">
          Thank you. MAKO will review your space and contact you.
        </h3>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-[#3B3B3B]">
          We will come back with suitable surface options and next steps. If your project is
          time-sensitive, message us on WhatsApp and reference your facility name.
        </p>
        <button
          type="button"
          data-testid="quote-form-reset"
          onClick={() => setDone(false)}
          className="mako-label mt-8 text-[#0F4C81] underline decoration-1 underline-offset-4"
        >
          Submit another enquiry
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="quote-form" noValidate className="space-y-10">
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={form.honeypot}
        onChange={set("honeypot")}
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Full name" required>
          <input
            className={inputCls}
            data-testid="quote-full-name"
            value={form.full_name}
            onChange={set("full_name")}
            placeholder="Your name"
          />
          {errors.full_name && (
            <span className="mt-2 block text-xs text-[#b3261e]">{errors.full_name}</span>
          )}
        </Field>
        <Field label="Gym or organisation">
          <input
            className={inputCls}
            data-testid="quote-organisation"
            value={form.organisation}
            onChange={set("organisation")}
            placeholder="Facility name"
          />
        </Field>
        <Field label="Phone" required>
          <input
            className={inputCls}
            data-testid="quote-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+91 00000 00000"
          />
          {errors.phone && (
            <span className="mt-2 block text-xs text-[#b3261e]">{errors.phone}</span>
          )}
        </Field>
        <Field label="Email" required>
          <input
            className={inputCls}
            data-testid="quote-email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@facility.com"
          />
          {errors.email && (
            <span className="mt-2 block text-xs text-[#b3261e]">{errors.email}</span>
          )}
        </Field>
        <Field label="City" required>
          <input
            className={inputCls}
            data-testid="quote-city"
            value={form.city}
            onChange={set("city")}
            placeholder="City"
          />
          {errors.city && (
            <span className="mt-2 block text-xs text-[#b3261e]">{errors.city}</span>
          )}
        </Field>
        <Field label="State">
          <input
            className={inputCls}
            data-testid="quote-state"
            value={form.state}
            onChange={set("state")}
            placeholder="State"
          />
        </Field>
      </div>

      <Field label="Type of facility">
        <div className="flex flex-wrap gap-2.5" data-testid="quote-facility-types">
          {FACILITY_TYPES.map((t) => (
            <Chip
              key={t}
              active={form.facility_type === t}
              onClick={() => setForm((f) => ({ ...f, facility_type: t }))}
            >
              {t}
            </Chip>
          ))}
        </div>
      </Field>

      <Field label="Sport or disciplines">
        <div className="flex flex-wrap gap-2.5" data-testid="quote-sports">
          {SPORT_OPTIONS.map((s) => (
            <Chip key={s} active={form.sports.includes(s)} onClick={() => toggle("sports", s)}>
              {s}
            </Chip>
          ))}
        </div>
      </Field>

      <Field label="Product interest">
        <div className="flex flex-wrap gap-2.5" data-testid="quote-products">
          {PRODUCT_OPTIONS.map((p) => (
            <Chip key={p} active={form.products.includes(p)} onClick={() => toggle("products", p)}>
              {p}
            </Chip>
          ))}
        </div>
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Approximate area" hint="For example 1,800 sq ft or 12 m × 10 m">
          <input
            className={inputCls}
            data-testid="quote-area"
            value={form.area}
            onChange={set("area")}
            placeholder="Approximate area"
          />
        </Field>
        <Field label="Required timeline">
          <div className="flex flex-wrap gap-2.5" data-testid="quote-timeline">
            {TIMELINES.map((t) => (
              <Chip
                key={t}
                active={form.timeline === t}
                onClick={() => setForm((f) => ({ ...f, timeline: t }))}
              >
                {t}
              </Chip>
            ))}
          </div>
        </Field>
      </div>

      <Field label="Message">
        <textarea
          className={`${inputCls} min-h-[140px] resize-y`}
          data-testid="quote-message"
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us about the room, the disciplines you run and anything fixed inside the space."
        />
      </Field>

      <Field label="Upload room images or floor plan" hint="Up to 3 images, 2 MB each. Optional.">
        <label className="mako-focus inline-flex cursor-pointer items-center gap-3 border border-dashed border-[#111111]/25 px-5 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.16em] text-[#3B3B3B] transition-colors duration-300 hover:border-[#0F4C81] hover:text-[#0F4C81]">
          <Upload className="h-4 w-4" />
          Choose files
          <input
            type="file"
            accept="image/*"
            multiple
            data-testid="quote-files"
            className="hidden"
            onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 3))}
          />
        </label>
        {files.length > 0 && (
          <ul className="mt-4 space-y-2">
            {files.map((f) => (
              <li key={f.name} className="flex items-center gap-3 text-xs text-[#3B3B3B]">
                {f.name}
                <button
                  type="button"
                  onClick={() => setFiles((prev) => prev.filter((p) => p.name !== f.name))}
                  className="text-[#7A7F85] hover:text-[#0F4C81]"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>

      <div className="flex flex-col gap-5 border-t border-[#111111]/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-sm text-xs leading-relaxed text-[#7A7F85]">
          We use your details only to respond to this enquiry. No marketing lists, no sharing.
        </p>
        <button
          type="submit"
          disabled={busy}
          data-testid="quote-submit"
          className="mako-focus inline-flex items-center justify-center gap-3 bg-[#0F4C81] px-9 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#1463A5] disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Sending" : "Send enquiry"}
        </button>
      </div>
    </form>
  );
};
